<template>
  <AppShell>
    <template #sidebar>
      <SidebarNav
        :user-name="displayName"
        :active-key="activeTab"
        :items="navItems"
        :role="sidebarRole"
        @select="onSidebarSelect"
      />
    </template>
    <template #header>
      <HeaderBar />
    </template>

    <div class="panel">
      <!-- Profile Completion Warning (show only for non-owner employees) -->
      <div v-if="!isOwner && !isProfileComplete && activeTab !== 'profile'" class="warning-section">
        <el-alert type="warning" :closable="false" show-icon class="completion-alert">
          <template #default>
            <div class="alert-content">
              <span>Your employee profile is incomplete. Complete the internal service coordination form to unlock case assignment and access all features.</span>
              <el-button type="warning" size="small" @click="goToProfileForm" class="alert-button">
                Complete Employee Profile
              </el-button>
            </div>
          </template>
        </el-alert>
      </div>

      <EmployeeCasesPanel
        v-if="activeTab === 'home'"
        scope="employee-home"
        :current-user="currentUser"
        :employee-profile="employeeProfile"
        :profile-incomplete="!isOwner && !isProfileComplete"
      />
      <EmployeeCasesPanel
        v-else-if="activeTab === 'active'"
        scope="employee-active"
        :current-user="currentUser"
        :employee-profile="employeeProfile"
        :profile-incomplete="!isOwner && !isProfileComplete"
      />
      <EmployeeCasesPanel
        v-else-if="activeTab === 'past'"
        scope="employee-past"
        :current-user="currentUser"
        :employee-profile="employeeProfile"
        :profile-incomplete="!isOwner && !isProfileComplete"
      />
      <OwnerToolsPanel
        v-else-if="activeTab === 'owner-tools' && isOwner"
        :current-user="currentUser"
        :owner-profile="employeeProfile"
      />
      <EmployeeProfilePanel
        v-else-if="activeTab === 'profile'"
        :profile="employeeProfileData"
        :profile-completed="isProfileComplete"
        @edit-profile="goToProfileForm"
      />
      <ComingSoonPanel
        v-else-if="activeTab === 'earnings'"
        eyebrow="Employee Earnings"
        title="Coming soon"
        message="Earnings and payouts will be available here once that workflow is ready."
      />

      <EmployeeProfileForm
        v-if="profileFormVisible"
        :model-value="profileFormVisible"
        :user-profile="employeeProfile"
        @form-closed="onProfileFormClosed"
        @form-submitted="onProfileFormSubmitted"
      />
    </div>
  </AppShell>
</template>

<script>
import { Collection, CreditCard, Document, House, Setting, User } from '@element-plus/icons-vue'
import { signOut } from 'firebase/auth'
import { auth } from '@/firebase'
import AppShell from '@/layouts/AppShell.vue'
import SidebarNav from '@/components/SidebarNav.vue'
import HeaderBar from '@/components/HeaderBar.vue'
import EmployeeCasesPanel from '@/components/EmployeeCasesPanel.vue'
import EmployeeProfilePanel from '@/components/EmployeeProfilePanel.vue'
import EmployeeProfileForm from '@/components/EmployeeProfileForm.vue'
import ComingSoonPanel from '@/components/ComingSoonPanel.vue'
import { CASE_SCOPES, watchCount } from '@/services/requests'
import OwnerToolsPanel from '@/components/OwnerToolsPanel.vue'
import {
  getUserDisplayName,
  getUserRoleFromProfile,
  resolveUserProfile,
  isEmployeeProfileComplete,
  getEmployeeProfile,
} from '@/services/userProfile'

export default {
  name: 'EmployeeDashboard',
  components: {
    AppShell,
    SidebarNav,
    HeaderBar,
    EmployeeCasesPanel,
    EmployeeProfilePanel,
    EmployeeProfileForm,
    OwnerToolsPanel,
    ComingSoonPanel,
  },
  props: {
    currentUser: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeTab: 'home',
      activeCount: 0,
      pastCount: 0,
      displayName: '',
      employeeProfile: null,
      profileFormVisible: false,
      unsubs: { active: null, past: null },
    }
  },
  computed: {
    isOwner() {
      return getUserRoleFromProfile(this.employeeProfile) === 'Owner'
    },
    sidebarRole() {
      return this.isOwner ? 'Owner' : 'Admin'
    },
    isProfileComplete() {
      return isEmployeeProfileComplete(this.employeeProfile)
    },
    employeeProfileData() {
      return getEmployeeProfile(this.employeeProfile)
    },
    navItems() {
      const items = [
        { key: 'home', label: 'Home', icon: House },
        { key: 'active', label: 'Active Requests', icon: Document, badge: this.activeCount },
        { key: 'past', label: 'Past Requests', icon: Collection, badge: this.pastCount },
      ]

      if (this.isOwner) {
        items.push({ key: 'owner-tools', label: 'Owner Tools', icon: Setting })
      }

      items.push(
        { key: 'profile', label: 'Profile', icon: User },
        { key: 'earnings', label: 'Earnings', icon: CreditCard },
      )

      return items
    },
  },
  async mounted() {
    await this.loadEmployeeProfile()
    this.subscribeCounts()
  },
  beforeUnmount() {
    this.unsubs.active && this.unsubs.active()
    this.unsubs.past && this.unsubs.past()
  },
  methods: {
    async loadEmployeeProfile() {
      const user = this.currentUser || auth.currentUser
      this.employeeProfile = await resolveUserProfile(user).catch(() => null)
      this.displayName = getUserDisplayName(this.employeeProfile, user)
    },
    subscribeCounts() {
      const uid = this.currentUser?.uid || auth.currentUser?.uid
      if (!uid) return

      this.unsubs.active && this.unsubs.active()
      this.unsubs.past && this.unsubs.past()

      this.unsubs.active = watchCount({ scope: CASE_SCOPES.EMPLOYEE_ACTIVE, uid }, (count) => {
        this.activeCount = count
      })
      this.unsubs.past = watchCount({ scope: CASE_SCOPES.EMPLOYEE_PAST, uid }, (count) => {
        this.pastCount = count
      })
    },
    onSidebarSelect(key) {
      if (key === 'logout') {
        this.logout()
        return
      }
      if (key === 'owner-tools' && !this.isOwner) return
      this.activeTab = key
    },
    async goToProfileForm() {
      await this.loadEmployeeProfile()
      this.profileFormVisible = true
    },
    onProfileFormClosed() {
      this.profileFormVisible = false
      this.loadEmployeeProfile()
    },
    onProfileFormSubmitted() {
      this.profileFormVisible = false
      this.loadEmployeeProfile()
    },
    async logout() {
      try {
        await signOut(auth)
      } catch (error) {}
      this.$router.push({ name: 'Login' })
    },
  },
}
</script>

<style scoped>
.panel {
  margin-top: 80px;
  box-sizing: border-box;
}

.warning-section {
  padding: 16px 24px;
}

.completion-alert {
  border-radius: 12px;
}

.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.alert-button {
  flex-shrink: 0;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .alert-content {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
